// Stage 5N-13.1 UX — centralized GSTIN-verification error → modal copy.
//
// Every GSTIN-related failure on the signup start page now resolves to
// a SINGLE, consistent professional modal via this helper. The previous
// behaviour leaned on the generic notifyError CODE_MAP path, which
// produced inconsistent wording ("your GSTIN was not rejected" was the
// most user-confusing example).
//
// Design notes:
//   - The signup START backend collapses provider-side
//     invalid/inactive/cancelled/suspended into a SINGLE
//     `GSTIN_NOT_ACTIVE` code (Stage 5N-12 strict-ACT rule). The error
//     response carries `details.gstinStatus` ('invalid' | 'inactive')
//     so this helper can render the right user-facing copy without the
//     backend leaking which bucket it was — same code on the wire,
//     finer-grained UX in the browser.
//   - Network / timeout errors don't have `err.response.data.error`;
//     we identify them by the same axios shape `notify.js` already
//     uses (`err.code === 'ECONNABORTED'` or no `err.response`).
//   - `severity` drives whether modal.error or modal.warning is used
//     (the existing notify.js convention).
//   - `focusGstin: true` tells the caller to refocus the GSTIN input
//     after the modal closes — used for format-invalid + not-found
//     where the user's next action is to fix the number.
//   - We never render the backend `code` directly to the user; the
//     code is converted to friendly copy here (or falls through to
//     the generic UNABLE_TO_VERIFY entry).

const SUPPORT_LINE =
  'If the issue continues, contact support at +91 98433 13491 / +91 96004 05305 or support@glass-i.in.';

// Single source of truth for every code → copy mapping. Keep messages
// short, professional, and action-oriented. Buttons are imperative
// verbs that match the user's next move.
const TEMPLATES = Object.freeze({
  FORMAT_INVALID: {
    title: 'Invalid GSTIN format',
    message:
      'The GSTIN entered does not appear to be valid. Please check the 15-character GSTIN and try again.',
    okText: 'Check again',
    severity: 'warning',
    focusGstin: true
  },
  NOT_FOUND: {
    title: 'GSTIN not found',
    message:
      'We could not find this GSTIN in the GST records. Please verify the GSTIN and enter it exactly as shown on your GST certificate.',
    okText: 'Edit GSTIN',
    severity: 'error',
    focusGstin: true
  },
  INACTIVE: {
    title: 'GSTIN is not active',
    message:
      'This GSTIN appears to be inactive, cancelled, or suspended. Please use an active GSTIN to continue.',
    okText: 'Edit GSTIN',
    severity: 'error',
    focusGstin: true
  },
  PROVIDER_UNAVAILABLE: {
    title: 'GST verification temporarily unavailable',
    message:
      'The GST verification service is not responding right now. This is a temporary issue with the GST portal — please try again in a few minutes.',
    okText: 'Try again',
    severity: 'warning',
    focusGstin: false
  },
  TIMEOUT: {
    title: 'Verification timed out',
    message:
      'The GSTIN verification request took too long to complete. Please check your connection and try again.',
    okText: 'Try again',
    severity: 'warning',
    focusGstin: false
  },
  LEGAL_NAME_MISMATCH: {
    title: 'Company details need review',
    message:
      'The GSTIN was found, but the registered legal name does not appear to match the company details entered. Please review the details before continuing.',
    okText: 'Review details',
    severity: 'warning',
    focusGstin: false
  },
  PROVIDER_AUTH_FAILED: {
    title: 'GST verification temporarily unavailable',
    message:
      'GSTIN verification is currently unavailable. Our team has been notified — please try again in a few minutes.',
    okText: 'Try again',
    severity: 'warning',
    focusGstin: false
  },
  UNABLE_TO_VERIFY: {
    title: 'Unable to verify GSTIN',
    message:
      'We could not complete GSTIN verification right now. Please try again. ' + SUPPORT_LINE,
    okText: 'Try again',
    severity: 'error',
    focusGstin: false
  }
});

function pickByCodeAndDetails(code, details) {
  // GSTIN_NOT_ACTIVE collapses both "doesn't exist" and "exists but
  // inactive". Disambiguate via details.gstinStatus when available,
  // and fall back to INACTIVE when the bucket is unknown — that's the
  // safer default copy (it doesn't accuse the user of typing a wrong
  // number when the provider just labelled it as cancelled).
  if (code === 'GSTIN_NOT_ACTIVE') {
    const bucket = details && typeof details.gstinStatus === 'string'
      ? details.gstinStatus
      : null;
    if (bucket === 'invalid') return TEMPLATES.NOT_FOUND;
    return TEMPLATES.INACTIVE;
  }

  if (code === 'GSTIN_NOT_FOUND')          return TEMPLATES.NOT_FOUND;
  if (code === 'GSTIN_INACTIVE')           return TEMPLATES.INACTIVE;
  if (code === 'GSTIN_FORMAT_INVALID')     return TEMPLATES.FORMAT_INVALID;
  if (code === 'INVALID_GSTIN')            return TEMPLATES.FORMAT_INVALID;
  if (code === 'GSTIN_INVALID')            return TEMPLATES.FORMAT_INVALID;

  if (code === 'GSTIN_PROVIDER_TIMEOUT')   return TEMPLATES.TIMEOUT;
  if (code === 'GSTIN_VERIFICATION_TIMEOUT') return TEMPLATES.TIMEOUT;

  if (code === 'GSTIN_PROVIDER_UNAVAILABLE') return TEMPLATES.PROVIDER_UNAVAILABLE;
  if (code === 'GSTIN_PROVIDER_ERROR')       return TEMPLATES.PROVIDER_UNAVAILABLE;
  if (code === 'GSTIN_PROVIDER_AUTH_FAILED') return TEMPLATES.PROVIDER_AUTH_FAILED;

  if (code === 'GSTIN_LEGAL_NAME_MISMATCH') return TEMPLATES.LEGAL_NAME_MISMATCH;

  if (code === 'GSTIN_VERIFICATION_FAILED') return TEMPLATES.UNABLE_TO_VERIFY;

  return null; // caller decides what fallback to show
}

// Detect a network / browser-side timeout from the raw axios error.
// Mirrors the same logic notify.js already uses for classifyByStatus
// so we stay consistent with the rest of the app.
function isNetworkOrTimeoutError(err) {
  if (!err) return false;
  if (err.code === 'ECONNABORTED') return true;
  if (err.message === 'Network Error') return true;
  if (err.response) return false;
  return true;
}

/**
 * Map a signup-start error to a modal config.
 *
 * @param {object} err   The axios / fetch error from endpoints.signup.start.
 * @returns {{
 *   title:      string,
 *   message:    string,
 *   subtext?:   string,
 *   okText:     string,
 *   severity:   'error' | 'warning',
 *   focusGstin: boolean,
 *   matched:    boolean   // true when we matched a known GSTIN-bucket;
 *                          // false means fall back to the generic
 *                          // notifyError pipeline (e.g. SIGNUP_IN_PROGRESS,
 *                          // SEZ_DECLARATION_REQUIRED, etc.).
 * }}
 */
export function getGstinErrorConfig(err) {
  // Browser-side network / timeout errors come BEFORE we ever reach
  // the backend, so there is no `code`. Treat as TIMEOUT.
  const errResp = err && err.response && err.response.data && err.response.data.error;
  const code    = errResp && errResp.code;
  const details = errResp && errResp.details;

  if (!code && isNetworkOrTimeoutError(err)) {
    return { ...TEMPLATES.TIMEOUT, matched: true };
  }

  const tpl = pickByCodeAndDetails(code, details);
  if (!tpl) return { matched: false };
  return { ...tpl, matched: true };
}

// Exposed for tests + future maintainers — read-only.
export const GSTIN_ERROR_TEMPLATES = TEMPLATES;
